import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRightLeft, Ban, CheckCircle2, Hand, RefreshCw } from 'lucide-react';
import type {
  Mcp,
  McpToolsResponse,
  McpToolState,
  McpToolWithStates,
} from '@echo-work-platform/shared-types';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';

// Claude.ai-style tri-state icon control: check = always allow, hand = needs
// approval, ban = blocked. The label + hint surface as the hover tooltip.
const STATE_OPTIONS: {
  value: McpToolState;
  label: string;
  hint: string;
  Icon: typeof CheckCircle2;
}[] = [
  {
    value: 'allow',
    label: 'Mindig engedélyezett',
    hint: 'A tool jóváhagyás nélkül futhat.',
    Icon: CheckCircle2,
  },
  {
    value: 'ask',
    label: 'Jóváhagyás szükséges',
    hint: 'Minden hívás előtt a felhasználónak jóvá kell hagynia.',
    Icon: Hand,
  },
  { value: 'deny', label: 'Tiltva', hint: 'A tool nem hívható.', Icon: Ban },
];

function StateToggle({
  value,
  onChange,
}: {
  value: McpToolState;
  onChange: (state: McpToolState) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full bg-neutral-100 p-1">
      {STATE_OPTIONS.map(({ value: state, label, hint, Icon }) => {
        const active = value === state;
        return (
          <button
            key={state}
            type="button"
            title={`${label} — ${hint}`}
            aria-label={label}
            aria-pressed={active}
            onClick={() => onChange(state)}
            className={cn(
              'rounded-full p-1.5 transition-colors',
              active
                ? 'bg-card text-neutral-900 shadow ring-1 ring-neutral-200'
                : 'text-neutral-400 hover:text-neutral-600',
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

function ToolTable({
  title,
  tools,
  boundSegments,
  onSetState,
  onReclassify,
  reclassifyLabel,
}: {
  title: string;
  tools: McpToolWithStates[];
  boundSegments: { id: string; name: string }[];
  onSetState: (toolId: string, segmentId: string, state: McpToolState) => void;
  onReclassify: (toolId: string, readOnly: boolean) => void;
  reclassifyLabel: string;
}): JSX.Element {
  return (
    <div className="card overflow-x-auto">
      <div className="border-b border-neutral-200 px-4 py-2 text-sm font-semibold">{title}</div>
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-72" />
          {boundSegments.map((s) => (
            <col key={s.id} className="w-52" />
          ))}
        </colgroup>
        <thead className="bg-neutral-50 text-xs text-neutral-500">
          <tr className="divide-x divide-neutral-200">
            <th className="px-4 py-2 text-left font-medium">Tool</th>
            {boundSegments.map((s) => (
              <th key={s.id} className="px-2 py-2 text-center font-medium">
                <span className="block truncate" title={s.name}>
                  {s.name}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {tools.map((t) => (
            <tr key={t.id} className="divide-x divide-neutral-100 hover:bg-neutral-50/60">
              <td className={cn('px-4 py-2', !t.presentInLastDiscovery && 'opacity-50')}>
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium text-neutral-700" title={t.toolName}>
                    {t.title ?? t.toolName}
                  </span>
                  <button
                    type="button"
                    title={reclassifyLabel}
                    aria-label={reclassifyLabel}
                    onClick={() => onReclassify(t.id, !t.readOnly)}
                    className="shrink-0 rounded p-0.5 text-neutral-300 hover:bg-neutral-100 hover:text-neutral-600"
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="truncate text-xs text-muted-foreground" title={t.description ?? undefined}>
                  {t.toolName}
                  {!t.presentInLastDiscovery && (
                    <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-neutral-500">
                      már nem elérhető
                    </span>
                  )}
                </div>
              </td>
              {boundSegments.map((s) => (
                <td key={s.id} className="px-2 py-2 text-center">
                  <StateToggle
                    value={t.states[s.id] ?? 'ask'}
                    onChange={(state) => onSetState(t.id, s.id, state)}
                  />
                </td>
              ))}
            </tr>
          ))}
          {tools.length === 0 && (
            <tr>
              <td colSpan={boundSegments.length + 1} className="px-4 py-4 text-center text-neutral-400">
                Nincs tool.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function McpTools(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const toolsEndpoint = `/api/admin/mcps/${id}/tools`;

  const mcpList = useQuery({
    queryKey: ['/api/admin/mcps'],
    queryFn: () => api.get<Mcp[]>('/api/admin/mcps'),
  });
  const mcp = mcpList.data?.find((m) => m.id === id);

  const tools = useQuery({
    queryKey: [toolsEndpoint],
    queryFn: () => api.get<McpToolsResponse>(toolsEndpoint),
    enabled: !!id,
  });

  const refresh = useMutation({
    mutationFn: () =>
      api.post<{ discoveredAt: string | null; error: string | null; toolCount: number }>(
        `/api/admin/mcps/${id}/refresh-tools`,
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [toolsEndpoint] });
      void queryClient.invalidateQueries({ queryKey: ['/api/admin/mcps'] });
    },
  });

  const setState = useMutation({
    mutationFn: (v: { toolId: string; segmentId: string; state: McpToolState }) =>
      api.put(`/api/admin/mcps/${id}/tools/${v.toolId}/policy`, {
        segmentId: v.segmentId,
        state: v.state,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [toolsEndpoint] }),
  });

  const reclassify = useMutation({
    mutationFn: (v: { toolId: string; readOnly: boolean }) =>
      api.patch(`/api/admin/mcps/${id}/tools/${v.toolId}`, { readOnly: v.readOnly }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [toolsEndpoint] }),
  });

  // First open of a never-discovered connector: run the discovery automatically
  // so the admin lands on a populated list instead of an empty table. Guarded
  // to a single attempt; a stored discovery error is surfaced, not retried.
  const autoDiscoverAttempted = useRef(false);
  useEffect(() => {
    if (autoDiscoverAttempted.current) return;
    if (!tools.data) return;
    if (tools.data.discoveredAt === null && tools.data.discoveryError === null) {
      autoDiscoverAttempted.current = true;
      refresh.mutate();
    }
  }, [tools.data, refresh]);

  const data = tools.data;
  const readOnlyTools = (data?.tools ?? []).filter((t) => t.readOnly);
  const writeTools = (data?.tools ?? []).filter((t) => !t.readOnly);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/mcps" className="rounded p-1 text-neutral-500 hover:bg-neutral-100">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h2 className="text-xl font-semibold text-heading">
              {mcp ? `${mcp.name} — toolok` : 'MCP toolok'}
            </h2>
            {data?.discoveredAt && (
              <p className="text-xs text-muted-foreground">
                Utolsó felderítés: {new Date(data.discoveredAt).toLocaleString('hu-HU')}
                {data.discoverySource === 'CLIENT' && ' — kliens-visszajelentésből'}
              </p>
            )}
          </div>
        </div>
        <button className="btn-secondary" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw className={cn('h-4 w-4', refresh.isPending && 'animate-spin')} /> Toolok frissítése
        </button>
      </div>

      {data?.discoveryError && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          A felderítés nem sikerült: {data.discoveryError}
        </p>
      )}
      {refresh.isError && <p className="text-sm text-rose-600">{String(refresh.error)}</p>}
      {setState.isError && <p className="text-sm text-rose-600">{String(setState.error)}</p>}
      {reclassify.isError && <p className="text-sm text-rose-600">{String(reclassify.error)}</p>}
      {tools.isLoading && <p className="text-sm text-neutral-500">Betöltés…</p>}
      {tools.isError && <p className="text-sm text-rose-600">Hiba: {String(tools.error)}</p>}

      {data && data.boundSegments.length === 0 && (
        <p className="text-sm text-muted-foreground">
          A connector nincs szegmenshez rendelve — előbb a{' '}
          <Link to="/bindings" className="text-accent-700 underline">
            Bindings
          </Link>{' '}
          oldalon rendeld hozzá, utána állítható toolonkénti engedély.
        </p>
      )}

      {data && data.boundSegments.length > 0 && (
        <>
          <ToolTable
            title="Csak olvasás"
            tools={readOnlyTools}
            boundSegments={data.boundSegments}
            onSetState={(toolId, segmentId, state) => setState.mutate({ toolId, segmentId, state })}
            onReclassify={(toolId, readOnly) => reclassify.mutate({ toolId, readOnly })}
            reclassifyLabel="Átsorolás az Írás / törlés csoportba"
          />
          <ToolTable
            title="Írás / törlés"
            tools={writeTools}
            boundSegments={data.boundSegments}
            onSetState={(toolId, segmentId, state) => setState.mutate({ toolId, segmentId, state })}
            onReclassify={(toolId, readOnly) => reclassify.mutate({ toolId, readOnly })}
            reclassifyLabel="Átsorolás a Csak olvasás csoportba"
          />
        </>
      )}
    </div>
  );
}
